import React from 'react';
import { generateQRMatrix } from '../utils/generators';
import { amountToWords } from '../utils/numberToWords';
interface LineItem {
  id: string;
  description: string;
  qty: number;
  rate: number;
  arabicName?: string;
  customColAr?: Record<string, string>;
  taxRate?: number;
  taxName?: string;
  b2bTaxRate?: number;
  b2bTaxName?: string;
  taxInclusive?: boolean;
}

interface TaxLine { label: string; rate: number; }
interface DiscountLine { label: string; rate: number; }

interface POSReceiptRendererProps {
  template: any;
  clientName: string;
  clientNameAr?: string;
  clientLineAr?: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  buyerTaxId?: string;
  productTaxName?: string;
  invoiceDate: string;
  invoiceTime: string;
  notes: string;
  lineItems: LineItem[];
  taxLines: TaxLine[];
  discountLines: DiscountLine[];
  sym: string;
  shippingCost: number;
  invoiceStatus?: string;
  translateTargetLang?: string;
  isBilingual?: boolean;
  isB2B?: boolean;
  isThermal?: boolean;
  showTokenQRPlaceholder?: boolean;
  isEditPreview?: boolean;
  showWalkIn?: boolean;
  containerWidthProp?: number;
  dynamicValues?: Record<string, string>;
  dynamicValuesAr?: Record<string, string>;
  dynamicFieldLabelsAr?: Record<string, string>;
  showAmountInWords?: boolean;
  amountInWords?: string;
  amountInWordsTranslated?: string;
  productTaxTotal?: number;
  autoDiscountAmount?: number;
  autoDiscountLabel?: string;
  netSubtotal?: number;
  notesAr?: string;
  rowLabelsAr?: { subtotal?: string; total?: string; tax?: string; shipping?: string };
  totalsLabels?: { subtotal?: string; total?: string; tax?: string; discount?: string; shipping?: string };
  taxLinesAr?: { label: string; labelAr?: string }[];
  discountLinesAr?: { label: string; labelAr?: string }[];
  // Named GST/VAT/QST breakdown (CGST/SGST/IGST/QST/etc.) — computed by the
  // caller (this component has no taxRate/taxInclusive per line item, so it
  // can't derive this itself). When omitted, falls back to the old blended
  // "Total Tax (X%)" line built from productTaxTotal.
  taxBreakdown?: { name: string; rate: number; amount: number }[];
  // Transliterated component names (CGST -> سي جي إس تي, etc.), keyed by
  // the English name — these are acronyms, not dictionary words, so they
  // go through transliteration, same as GST/VAT elsewhere in the app.
  taxBreakdownAr?: Record<string, string>;
  // Extra columns picked with "Add Col" (values are read from each line item by key)
  extraColumns?: { id?: string; key: string; header: string; align?: string; after?: string }[];
}

const RTL_LANGS = ['ar', 'ur', 'fa', 'iw', 'he', 'ckb', 'ku', 'ps', 'sd', 'dv', 'ug', 'yi'];
const isRTL = (lang?: string) => RTL_LANGS.includes((lang || '').toLowerCase());
const arNum = (s: string) => s.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);

// Normalizes digits: converts to Eastern Arabic ONLY for Arabic ('ar');
// for Hindi ('hi') and all other languages, ensures standard digits (0-9)
function normalizeDigitsForLang(text: string, lang?: string): string {
  if (!text) return '';
  if (lang === 'ar') return arNum(text);
  // Strip both Arabic-Indic and Devanagari numerals back to standard 0-9
  return text
    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[०-९]/g, d => String('०१२३४५६७८९'.indexOf(d)));
}
function patchArabicDynamicValue(
  arabicText: string,
  key: string,
  englishLabelPattern: string,
  arabicLabel: string | undefined,
  fillValue: string
): string {
  let out = arabicText;

  // Only replace explicit placeholders (brackets, underscores, dashes) to prevent duplicating already-translated text
  if (arabicLabel) {
    const esc = arabicLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(
      new RegExp(`(${esc})\\s*:?\\s*(\\[[^\\]]*\\]|_{2,}|[-—]{1,3})`, 'g'),
      (_m, l) => `${l}: ${fillValue}`
    );
  }

  out = out.replace(
    new RegExp(`(${englishLabelPattern})\\s*:\\s*(\\[[^\\]]*\\]|_{2,}|[-—]{1,3})`, 'gi'),
    (_m, l) => `${l}: ${fillValue}`
  );

  return out;
}

// LRI (U+2066) ... PDI (U+2069) — Unicode bidi isolate marks. Wrapping a
// Latin/digit value (invoice number, order code, etc.) in these before
// inserting it into an Arabic string tells the renderer "treat this
// exact run as an independent left-to-right block" — so it displays as
// the identical, unreversed string ("POS-REC-00017") instead of getting
// visually split/reordered by the surrounding RTL text.
const ltrIsolate = (s: string) => `\u2066${s}\u2069`;

function patchDynamicFieldsIntoElements(
  elements: any[],
  dynamicValues?: Record<string, string>,
  dynamicValuesAr?: Record<string, string>,
  dynamicFieldLabelsAr?: Record<string, string>
) {
  if (!dynamicValues || !Object.keys(dynamicValues).length) return elements;

  const staffVal = dynamicValues?.staff_name || dynamicValues?.cashier || dynamicValues?.staff || '';
  const arStaffVal = dynamicValuesAr?.['staffName_translit'] || dynamicValuesAr?.['staff_name_translit'] || dynamicValuesAr?.['staff_name'] || staffVal;

  return elements.map((el: any) => {
    // 1. Signature support
    if (el.type === 'signature') {
      const sig = (el.signatureName || staffVal).replace(/\{staff_name\}/gi, staffVal);
      return { ...el, signatureName: sig };
    }

    // 2. Dynamic Barcode & QR support
    if (el.type === 'barcode' && el.barcodeValue) {
      let bVal = el.barcodeValue;
      for (const [key, rawValue] of Object.entries(dynamicValues)) {
        if (!rawValue?.trim()) continue;
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        bVal = bVal.replace(new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'), rawValue.trim());
      }
      return { ...el, barcodeValue: bVal };
    }

    if (el.type !== 'text' || !el.textContent) return el;

    let text = el.textContent;
    let arabicText = el.arabicContent;

    // Direct universal replacement for {staff_name}
    if (staffVal) {
      text = text.replace(/\{staff_name\}/gi, staffVal);
      if (arabicText) {
        arabicText = arabicText.replace(/\{staff_name\}/gi, ltrIsolate(arStaffVal));
      }
    }

    for (const [key, rawValue] of Object.entries(dynamicValues)) {
      const value = rawValue?.trim();
      if (!value) continue;

      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const labelPattern = key
        .split('_')
        .filter(Boolean)
        .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('[\\s\\/\\.\\(\\)#_-]*');

      // English side — same patterns as QuickInvoiceView's patchDynamicFields
      text = text.replace(new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'), value);
      text = text.replace(new RegExp(`(${labelPattern})\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_m: string, l: string) => `${l}: ${value}`);
      text = text.replace(new RegExp(`(${labelPattern})\\s*:\\s*_{2,}`, 'gi'), (_m: string, l: string) => `${l}: ${value}`);
      text = text.replace(new RegExp(`(${labelPattern})\\s*:\\s*[-—]{1,3}`, 'gi'), (_m: string, l: string) => `${l}: ${value}`);

      // Arabic side — fill with the translated value if we have one, else
      // fall back to the raw value. Either way, isolate it: whether it's
      // a genuinely translated word or a raw Latin code like an invoice
      // number, wrapping it keeps that exact run intact and correctly
      // ordered inside the surrounding RTL line.
      if (arabicText) {
        const arValue = ltrIsolate(dynamicValuesAr?.[key] || value);
        arabicText = arabicText.replace(new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'), arValue);
        const arLabel = dynamicFieldLabelsAr?.[key];
        arabicText = patchArabicDynamicValue(arabicText, key, labelPattern, arLabel, arValue);
      }
    }

    if (arabicText) {
      arabicText = arabicText.replace(/\(?PropertyNameError\)?/gi, '').trim();
    }

    if (text === el.textContent && arabicText === el.arabicContent) return el;
    return { ...el, textContent: text, arabicContent: arabicText };
  });
}

function groupOverlappingShapes(els: any[]) {
  // Only consider real container boxes (ignore thin vertical accent stripes <= 25px wide)
  const shapes = els.filter(
    (el: any) =>
      el.type === 'shape' &&
      el.backgroundColor &&
      el.backgroundColor !== 'transparent' &&
      (el.width || 0) > 25 &&
      (el.height || 0) > 10
  );
  const consumedIds = new Set<string>();
  const groups: { shape: any; children: any[] }[] = [];

  shapes.forEach((shape: any) => {
    const sTop = shape.y || 0, sBottom = sTop + (shape.height || 0);
    const sLeft = shape.x || 0, sRight = sLeft + (shape.width || 0);
    const children = els.filter((el: any) => {
      if (el.id === shape.id || consumedIds.has(el.id)) return false;
      const eTop = el.y || 0, eBottom = eTop + (el.height || 0);
      const eLeft = el.x || 0, eRight = eLeft + (el.width || 0);
      return eTop < sBottom && eBottom > sTop && eLeft < sRight && eRight > sLeft;
    });
    // ONLY group shapes if they actually contain text/children. Never render empty giant shapes!
    if (children.length > 0) {
      children.forEach((c: any) => consumedIds.add(c.id));
      consumedIds.add(shape.id);
      groups.push({ shape, children });
    }
  });

  const remaining = els.filter((el: any) => !consumedIds.has(el.id));
  return { groups, remaining };
}

function extractSections(template: any) {
  const elements = (template?.elements || []).sort((a: any, b: any) => a.y - b.y);
  const tableEl = elements.find((el: any) => el.type === 'table');
  const tableY = tableEl ? tableEl.y : 999999;
  const headerEls = elements.filter((el: any) => el.y < tableY && el.type !== 'table');
  const footerEls = elements.filter((el: any) => el.y > tableY && el.type !== 'table');
  return { headerEls, tableEl, footerEls };
}

// 100% Real Phone-Scannable QR Component
function RealScannableQR({ value, size = 80 }: { value: string; size?: number }) {
  const [dataUrl, setDataUrl] = React.useState<string>('');

  React.useEffect(() => {
    if (!value || value.startsWith('data:image')) {
      if (value) setDataUrl(value);
      return;
    }
    let cancelled = false;
    import('qrcode').then(QRCode => {
      QRCode.default.toDataURL(value, { width: size * 2, margin: 1 })
        .then(url => { if (!cancelled) setDataUrl(url); })
        .catch(() => {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [value, size]);

  if (!dataUrl) {
    return <div style={{ width: size, height: size, background: '#f8fafc', margin: '0 auto' }} />;
  }
  return <img src={dataUrl} alt="Scannable QR" style={{ width: size, height: size, display: 'block', margin: '0 auto' }} />;
}

function FlowElement({ el, lang, isBilingual, accentColor, autoQRSize }: { el: any; lang?: string; isBilingual?: boolean; accentColor?: string; autoQRSize?: number }) {
  const effectiveBilingual = !!isBilingual;
  const rtl = isRTL(lang);
  const targetFont = rtl ? '"Noto Sans Arabic", "Noto Sans", Arial, sans-serif' : 'inherit';

  if (el.type === 'image' && el.textContent) {
  const isQR = el.id?.startsWith('token-qr-') || el.id?.startsWith('invoice-qr-');
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '2px 0' }}>
      <img
        src={el.textContent}
        alt=""
        style={{
          width: isQR ? 80 : undefined,
          height: isQR ? 80 : undefined,
          maxWidth: isQR ? undefined : '100%',
          maxHeight: isQR ? undefined : 70,
          objectFit: 'contain',
        }}
      />
    </div>
  );
}
  if (el.type === 'qr') {
    const imgSize = el._auto ? (autoQRSize || 80) : (el.width || 80);
    if (el.imageDataUrl) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '4px 0', width: '100%' }}>
          <img src={el.imageDataUrl} alt="QR Code" style={{ width: imgSize, height: imgSize }} />
        </div>
      );
    }
    const qrVal = el.qrValue || el.textContent;
    if (!qrVal || qrVal === 'PREVIEW' || qrVal === 'preview') return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '4px 0', width: '100%' }}>
        <RealScannableQR value={qrVal} size={imgSize} />
      </div>
    );
  }

  if (el.type === 'shape') {
    const hasFill = el.backgroundColor && el.backgroundColor !== 'transparent';
    if (hasFill) {
      // If it's a vertical stripe (width <= 25px and height > 25px), don't expand it into a full-width block
      if ((el.width || 0) <= 25 && (el.height || 0) > 25) {
        return null;
      }
      return (
        <div
          style={{
            width: '100%',
            height: Math.min(el.height || 4, 30),
            backgroundColor: el.backgroundColor,
            margin: '4px 0',
          }}
        />
      );
    }
    const h = Math.max(1, el.borderWidth || 1);
    return <div style={{ borderTop: `${h}px solid ${el.borderColor || '#cbd5e1'}`, margin: '4px 0' }} />;
  }

  if (el.type === 'text') {
    const engText = el.textContent || '';
    let transText = el.arabicContent || '';

    // 1. Clean out any corrupted library or translation error strings
    if (transText) {
      transText = transText.replace(/\(?PropertyNameError\)?/gi, '').replace(/\s{2,}/g, ' ').trim();
    }

    // 2. Universal Symbol, Shape & Glyph Mirroring (+, ★, •, ✦, ➕, emojis, etc.)
    // If English header has shapes/symbols, automatically mirror them onto the translated text
    const symbolRegex = /^([\s+\-*\/=<>%&|^~!?:;.,_#@$€£¥₹§©®™°•·★☆✓✔✕✖✗✦✧❖▲▼►◄◆◇○●□■\u2000-\u206F\u2700-\u27BF\uE000-\uF8FF\uD83C-\uDBFF\uDC00-\uDFFF\(\)\[\]\{\}]+)/u;
    const trailSymbolRegex = /([\s+\-*\/=<>%&|^~!?:;.,_#@$€£¥₹§©®™°•·★☆✓✔✕✖✗✦✧❖▲▼►◄◆◇○●□■\u2000-\u206F\u2700-\u27BF\uE000-\uF8FF\uD83C-\uDBFF\uDC00-\uDFFF\(\)\[\]\{\}]+)$/u;

    const leadSym = engText.match(symbolRegex)?.[1] || '';
    const trailSym = engText.match(trailSymbolRegex)?.[1] || '';

    if (transText && (leadSym || trailSym)) {
      let core = transText;
      if (leadSym && core.startsWith(leadSym.trim())) core = core.slice(leadSym.trim().length);
      if (trailSym && core.endsWith(trailSym.trim())) core = core.slice(0, core.length - trailSym.trim().length);
      core = core.trim();
      if (core) {
        transText = `${leadSym}${core}${trailSym}`;
      }
    }

    // Convert both Hindi digits (०-९) and Arabic-Indic digits (٠-٩) back to normal digits (0-9) for non-Arabic
    if (transText && lang !== 'ar') {
      transText = normalizeDigitsForLang(transText, lang);
    }

    const hasTranslation = effectiveBilingual && transText && transText !== engText;

    if (hasTranslation) {
      return (
        <div style={{ display: 'flex', gap: 8, margin: '2px 0', alignItems: 'flex-start' }}>
          <div style={{
            flex: 1, fontSize: el.fontSize || 11,
            fontFamily: el.fontFamily || 'Inter, Arial, sans-serif',
            fontWeight: el.fontWeight || 'normal',
            color: el.color || '#111',
            textAlign: (el.textAlign as any) || 'left',
            whiteSpace: 'pre-wrap', lineHeight: el.lineHeight || 1.4, wordBreak: 'break-word',
          }}>{engText}</div>
          <div style={{
            flex: 1, fontSize: Math.round((el.fontSize || 11) * 0.9),
            fontFamily: targetFont,
            color: el.color || '#111',
            textAlign: rtl ? 'right' : 'left',
            direction: rtl ? 'rtl' : 'ltr',
            whiteSpace: 'pre-wrap', lineHeight: el.lineHeight || 1.4, wordBreak: 'break-word',
          }}>{lang === 'ar' ? arNum(transText) : transText}</div>
        </div>
      );
    }

    return (
      <div style={{
        fontSize: el.fontSize || 11,
        fontFamily: el.fontFamily || 'Inter, Arial, sans-serif',
        fontWeight: el.fontWeight || 'normal',
        fontStyle: el.fontStyle || 'normal',
        color: el.color || '#111',
        textAlign: (el.textAlign as any) || 'left',
        whiteSpace: 'pre-wrap', lineHeight: el.lineHeight || 1.4,
        wordBreak: 'break-word', margin: '2px 0',
      }}>{engText}</div>
    );
  }

  if (el.type === 'signature') {
    return (
      <div style={{ textAlign: (el.textAlign as any) || 'center', margin: '6px 0' }}>
        <div style={{ fontStyle: 'italic', fontWeight: 'bold', fontSize: el.fontSize || 9, color: el.color || '#111' }}>
          {el.signatureName}
        </div>
        {el.textContent && (
          <div style={{ fontSize: (el.fontSize || 9) * 0.85, color: '#6b7280' }}>
            {el.textContent}
          </div>
        )}
      </div>
    );
  }

  // Any custom or template stamp (PAID, FEES SETTLED, APPROVED, etc.)
  if (el.type === 'stamp') {
    const stampText = el.stampText || el.textContent || 'APPROVED';
    const stampColor = el.color || '#dc2626';
    const isAr = lang === 'ar';
    const transText = el.arabicContent || (el.type === 'stamp' && el.arabicText) || '';
    const hasTranslation = effectiveBilingual && transText && transText.trim().toLowerCase() !== stampText.trim().toLowerCase();

    return (
      <div style={{
        textAlign: 'center',
        margin: '8px auto',
        border: `3px double ${stampColor}`,
        borderRadius: el.stampShape === 'circle' || el.stampShape === 'oval' ? '9999px' : '6px',
        padding: '4px 10px',
        color: stampColor,
        fontWeight: 900,
        fontSize: el.fontSize || 12,
        letterSpacing: 2,
        textTransform: 'uppercase',
        display: 'block',
      }}>
        <div>{stampText}</div>
        {hasTranslation && (
          <div style={{
            fontSize: (el.fontSize || 12) * 0.75,
            fontFamily: targetFont,
            direction: rtl ? 'rtl' : 'ltr',
            fontWeight: 700,
            letterSpacing: 0,
            marginTop: 2,
          }}>
            {isAr ? arNum(transText) : transText}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default function POSReceiptRenderer({
  template, clientName, clientNameAr, clientLineAr, clientEmail, clientPhone, clientAddress, buyerTaxId, productTaxName,
  invoiceDate, invoiceTime, notes, lineItems, taxLines, discountLines,
  sym, shippingCost, invoiceStatus, translateTargetLang, isBilingual, isB2B = false, isThermal, showTokenQRPlaceholder, isEditPreview, showWalkIn = false,
  containerWidthProp,
  dynamicValues, dynamicValuesAr, dynamicFieldLabelsAr,
  showAmountInWords, amountInWords, amountInWordsTranslated,
  productTaxTotal = 0, autoDiscountAmount = 0, autoDiscountLabel, netSubtotal,
  notesAr, rowLabelsAr, totalsLabels, taxLinesAr, discountLinesAr, taxBreakdown, taxBreakdownAr, extraColumns,
}: POSReceiptRendererProps) {

  const patchedTemplateElements = React.useMemo(
    () => patchDynamicFieldsIntoElements(
      template?.elements || [],
      dynamicValues,
      dynamicValuesAr,
      dynamicFieldLabelsAr
    ),
    [template?.elements, dynamicValues, dynamicValuesAr, dynamicFieldLabelsAr]
  );
  template = { ...template, elements: patchedTemplateElements };

  const storedLang =
    template?.translated_lang ||
    template?.translatedLang ||
    (template as any)?.language ||
    template?.elements?.find((el: any) => el.translatedLang)?.translatedLang;

  // Active selected language MUST take priority over old saved template language!
  let rawLang = translateTargetLang || storedLang || 'en';

  // Sanity check: If the content is actually Hindi (Devanagari), do NOT allow stored 'ar' to hijack it!
  if (rawLang === 'ar') {
    const hasDevanagari =
      (template?.elements || []).some((el: any) => /[\u0900-\u097F]/.test(el.arabicContent || '')) ||
      Object.values(dynamicValuesAr || {}).some(v => /[\u0900-\u097F]/.test(String(v))) ||
      /[\u0900-\u097F]/.test(clientNameAr || '');
    if (hasDevanagari) {
      rawLang = 'hi';
    }
  }

  const lang = rawLang;
  const effectiveBilingual = !!(isBilingual ?? template?.is_bilingual ?? template?.isBilingual);
  const rtl = isRTL(lang);
  const targetFont = rtl
    ? '"Noto Sans Arabic", "Noto Sans", Arial, sans-serif'
    : lang === 'hi'
    ? '"Noto Sans Devanagari", "Noto Sans", Arial, sans-serif'
    : 'inherit';
  const arabicFont = targetFont;

  // Extract sections once at the top of the component
  const { headerEls, tableEl, footerEls: rawFooterEls } = extractSections(template);
  const tableRowLabels = (tableEl as any)?.rowLabelsAr || (tableEl as any)?.row_labels_ar;
  const tableDynamicValues = (tableEl as any)?.dynamic_values_ar || (tableEl as any)?.dynamicValuesAr;

  // Universal Fallback cascade across 100+ languages: Props -> Template Root -> Table Element -> Window cache
  const resolvedRowLabels =
    rowLabelsAr ||
    template?.row_labels_tr ||
    template?.rowLabelsTr ||
    template?.row_labels_ar ||
    template?.rowLabelsAr ||
    (tableEl as any)?.row_labels_tr ||
    (tableEl as any)?.rowLabelsTr ||
    tableRowLabels ||
    (typeof window !== 'undefined' ? ((window as any).__pos_row_labels_tr || (window as any).__pos_row_labels_ar) : undefined);

  const resolvedDynamicValues =
    dynamicValuesAr ||
    template?.dynamic_values_tr ||
    template?.dynamicValuesTr ||
    template?.dynamic_values_ar ||
    template?.dynamicValuesAr ||
    (tableEl as any)?.dynamic_values_tr ||
    (tableEl as any)?.dynamicValuesTr ||
    tableDynamicValues ||
    (typeof window !== 'undefined' ? ((window as any).__pos_dynamic_values_tr || (window as any).__pos_dynamic_values_ar) : undefined);

  const resolvedDynamicLabels =
    dynamicFieldLabelsAr ||
    template?.dynamic_field_labels_tr ||
    template?.dynamicFieldLabelsTr ||
    template?.dynamic_field_labels_ar ||
    template?.dynamicFieldLabelsAr ||
    (tableEl as any)?.dynamic_field_labels_tr ||
    (tableEl as any)?.dynamic_field_labels_ar ||
    (typeof window !== 'undefined' ? ((window as any).__pos_dynamic_field_labels_tr || (window as any).__pos_dynamic_field_labels_ar) : undefined);

  // Read translated labels dynamically without hardcoding any language
  const tl = {
    subtotal: resolvedRowLabels?.subtotal || totalsLabels?.subtotal || resolvedDynamicValues?.['label_subtotal'] || resolvedDynamicValues?.['subtotal'],
    total: resolvedRowLabels?.total || totalsLabels?.total || resolvedDynamicValues?.['label_total'] || resolvedDynamicValues?.['total'] || resolvedDynamicValues?.['label_gross_amount'],
    tax: resolvedRowLabels?.tax || totalsLabels?.tax || resolvedDynamicValues?.['label_tax'] || resolvedDynamicValues?.['tax_0'] || resolvedDynamicValues?.['tax'],
    discount: resolvedRowLabels?.discount || totalsLabels?.discount || resolvedDynamicValues?.['label_discount'] || resolvedDynamicValues?.['discount_0'] || resolvedDynamicValues?.['discount'],
    shipping: resolvedRowLabels?.shipping || totalsLabels?.shipping || resolvedDynamicValues?.['label_shipping'] || resolvedDynamicValues?.['shipping'],
  };

  const activeItems = lineItems.filter(li => li.description?.trim());
  // Per-item display in the table still uses qty*rate (the full price the customer sees per line).
  // But the Subtotal/Tax%/Discount%/TOTAL block must use the NET subtotal (tax excluded) —
  // otherwise inclusive-tax items get their tax counted twice once productTaxTotal is added back.
  const localSubtotal = activeItems.reduce((s, li) => s + li.qty * li.rate, 0);
  const subtotal = netSubtotal ?? localSubtotal;

  // Discount reduces the TAXABLE VALUE before tax is calculated —
  // standard GST/VAT invoicing math, matches every other totals block
  // in the app (QuickInvoiceView, computeTableTotals, BilingualInvoiceRenderer).
  const discTotal = discountLines.reduce((s, d) => s + subtotal * (d.rate / 100), 0);
  const totalDiscount = discTotal + autoDiscountAmount;
  const taxableValue = subtotal - totalDiscount;
  const taxTotal = taxLines.reduce((s, t) => s + taxableValue * (t.rate / 100), 0);
  const grandTotal = taxableValue + productTaxTotal + taxTotal + shippingCost;
  // ── Inject date/time into all text elements ──────────────────────────────
const dateTimeStr = `${invoiceDate}${invoiceTime ? '  ' + invoiceTime : ''}`;

const injectDateTime = (els: any[]) =>
  els.map(el => {
    if (el.type !== 'text' || !el.textContent) return el;
    let t = el.textContent;
    // Replace standard placeholders
    t = t
      .replace(/\{date\}/g, invoiceDate)
      .replace(/\{time\}/g, invoiceTime)
      .replace(/\{invoice_date\}/g, invoiceDate);

    // Replace existing date-like patterns
    t = t.replace(/Date\s*:\s*[\d\-\/\.]+/gi, `Date: ${invoiceDate}`);
    t = t.replace(/Date\s*&\s*Time\s*:\s*.+/gi, `Date & Time: ${dateTimeStr}`);
    t = t.replace(/Time\s*:\s*\d{1,2}:\d{2}(?:\s*[AaPp][Mm])?/g, `Time: ${invoiceTime}`);
    t = t.replace(/\b\d{4}[-\/]\d{2}[-\/]\d{2}\b/, invoiceDate);
    return t !== el.textContent ? { ...el, textContent: t } : el;
  });

const isCustomerTextElement = (el: any) => {
  if (el.type !== 'text' || !el.textContent) return false;
  const t = el.textContent.trim().toLowerCase();
  const firstLine = t.split('\n')[0].trim();
  const isClientTarget =
    el.id?.startsWith('synth-client-block') ||
    el.id?.startsWith('client-info-') ||
    t.includes('{client_details}') ||
    /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|sold\s*to|buyer|recipient)\s*:/i.test(firstLine);

  const cleanName = (clientName || '').trim().toLowerCase();
  const matchesCurrentClient =
    Boolean(cleanName && cleanName !== 'walk-in customer') &&
    (firstLine === cleanName || t.startsWith(cleanName));

  const cleanPhone = (clientPhone || '').replace(/\D/g, '');
  const cleanEmail = (clientEmail || '').trim().toLowerCase();
  const containsContactLines =
    (Boolean(cleanPhone) && t.replace(/\D/g, '').includes(cleanPhone)) ||
    (Boolean(cleanEmail) && t.includes(cleanEmail));

  return isClientTarget || (matchesCurrentClient && containsContactLines);
};

// Filter out duplicate client text elements from header so only ONE customer section renders
const patchedHeaderEls = injectDateTime(headerEls).filter((el: any) => !isCustomerTextElement(el));
const patchedFooterEls = injectDateTime(rawFooterEls /* before the filter */);
// Deduplicate footer elements so invoice QR/label and token QR/label only appear ONCE
const seenFooterKeys = new Set<string>();
const footerEls = patchedFooterEls.filter((el: any) => {
  if (el.id === 'pdf-amount-in-words' || el.id === 'preview-amount-in-words') return false;
  if (el.textContent && el.textContent.toLowerCase().startsWith('amount in words:')) return false;

  const isInvQr = el._auto === 'invoice' || el.id?.startsWith('invoice-qr');
  const isInvLabel = el._auto === 'invoice_label' || el.id?.startsWith('invoice-label') || (el.type === 'text' && el.textContent === 'Scan to view invoice');
  const isTokQr = el._auto === 'tracking' || el.id?.startsWith('token-qr');
  const isTokLabel = el._auto === 'tracking_label' || el.id?.startsWith('token-label') || (el.type === 'text' && /scan to track/i.test(el.textContent));

  const key = isInvQr ? 'inv-qr' : isInvLabel ? 'inv-label' : isTokQr ? 'tok-qr' : isTokLabel ? 'tok-label' : null;
  if (key) {
    if (seenFooterKeys.has(key)) return false;
    seenFooterKeys.add(key);
  }
  return true;
});

  const templateColumns: any[] = tableEl?.columns || [
    { key: 'description', header: 'Item', width: 50, align: 'left' },
    { key: 'qty', header: 'Qty', width: 15, align: 'left' },
    { key: 'rate', header: 'Rate', width: 17, align: 'left' },
    { key: 'total', header: 'Amt', width: 18, align: 'left' },
  ];

  // Extra columns from "Add Col": drop any copy the table already has, then
  // put each one right after the column the user chose (no choice = at the end).
  const extraKeys = new Set((extraColumns || []).map(c => c.key));
  const rawColumns: any[] = templateColumns.filter((c: any) => !extraKeys.has(c.key));
  (extraColumns || []).forEach(nc => {
    const prev = templateColumns.find((c: any) => c.key === nc.key);
    const col: any = { ...(prev || {}), id: nc.id || nc.key, key: nc.key, header: nc.header, originalHeader: nc.header, width: 12, align: 'left' };
    let idx = nc.after ? rawColumns.findIndex((c: any) => c.key === nc.after) : -1;
    if (idx >= 0) {
      while (idx + 1 < rawColumns.length && extraKeys.has(rawColumns[idx + 1].key)) idx++;
      rawColumns.splice(idx + 1, 0, col);
    } else {
      rawColumns.push(col);
    }
  });

  // Column widths are authored/saved assuming a 4-column table. Every extra
  // custom column (Batch No, Remarks, Hsn Code, ...) pushed the total past
  // 100% instead of shrinking the others, so table-layout:fixed just let
  // cells overflow the box. Normalize here: give non-description columns a
  // sane minimum share, then let description (Item) absorb/give back
  // whatever's left — it's the one column with room to spare.
  const MIN_COL_PCT = 12;
  const descCol = rawColumns.find((c: any) => c.key === 'description');
  const otherCols = rawColumns.filter((c: any) => c.key !== 'description');
  const otherTotal = otherCols.reduce((s: number, c: any) => s + Math.max(c.width || 0, MIN_COL_PCT), 0);
  const descWidth = Math.max(100 - otherTotal, MIN_COL_PCT);

  const columns = rawColumns.map((c: any) =>
    c.key === 'description'
      ? { ...c, width: descWidth, align: 'left' }
      : { ...c, width: Math.max(c.width || 0, MIN_COL_PCT), align: 'left' }
  );

  const headerColor = tableEl?.color || '#1e293b';
  const tableFont = tableEl?.fontFamily || 'Inter, Arial, sans-serif';
  const tableFontSize = tableEl?.fontSize || 11;
  const isAr = lang === 'ar';

  const statusColor = ['paid','Paid'].includes(invoiceStatus || '') ? '#10b981'
    : ['overdue','Overdue'].includes(invoiceStatus || '') ? '#ef4444'
    : ['cancelled','Cancelled'].includes(invoiceStatus || '') ? '#6b7280'
    : ['unpaid','Unpaid'].includes(invoiceStatus || '') ? '#f59e0b' : null;

  const fixedWidth = isThermal ? 302 : 620;
  const containerWidth = containerWidthProp ?? fixedWidth;
  const savedQrWidth = template?.elements?.find((e: any) => e.type === 'qr' && !e._auto)?.width;
  const autoQRSize = savedQrWidth || (containerWidthProp && containerWidthProp <= 220 ? 60 : 80);

  

  // Helper: translated label row (Universal for any of 100+ languages)
  const BiLabel = ({ eng, trans }: { eng: string; trans?: string }) => {
    const cleanTrans = normalizeDigitsForLang(trans || '', lang);
    return (
      <div>
        <div>{eng}</div>
        {effectiveBilingual && cleanTrans && cleanTrans !== eng && (
          <div style={{
            fontSize: tableFontSize * 0.8,
            fontFamily: targetFont,
            direction: rtl ? 'rtl' : 'ltr',
            textAlign: rtl ? 'right' : 'left',
            opacity: 0.85,
            marginTop: 1,
          }}>
            {cleanTrans}
          </div>
        )}
      </div>
    );
  };

  // Helper: bilingual number cell (English on top, Arabic numerals below ONLY when language is Arabic)
  const BiNum = ({ eng }: { eng: string }) => (
    <div>
      <div style={{ fontFamily: 'monospace', direction: 'ltr', unicodeBidi: 'embed' }}>{eng}</div>
      {effectiveBilingual && isAr && lang === 'ar' && (
        <div style={{ fontSize: tableFontSize * 0.82, fontFamily: arabicFont, direction: 'ltr', unicodeBidi: 'embed', color: '#64748b', marginTop: 1 }}>
          {arNum(eng)}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      width: '100%', maxWidth: containerWidth, margin: '0 auto', background: '#fff',
      fontFamily: 'Inter, Arial, sans-serif', fontSize: 11, color: '#111',
      padding: isThermal ? '6px 8px 8px' : '20px 24px', boxSizing: 'border-box', overflow: 'hidden',
    }}>

      {/* HEADER ELEMENTS */}
     
{/* HEADER ELEMENTS */}
<div style={{ marginBottom: 8 }}>
  {(() => {
    const { groups, remaining } = groupOverlappingShapes(patchedHeaderEls);
    return (
      <>
        {groups.map(g => (
          <div
            key={g.shape.id}
            style={{
              position: 'relative',
              width: '100%',
              height: g.shape.height || 80,
              backgroundColor: g.shape.backgroundColor,
              marginBottom: 6,
              overflow: 'visible',
            }}
          >
            {g.children.map((child: any) => (
              <div
                key={child.id}
                style={{ position: 'absolute', left: (child.x || 0) - (g.shape.x || 0), top: (child.y || 0) - (g.shape.y || 0), right: 0 }}
              >
                <FlowElement el={child} lang={lang} isBilingual={effectiveBilingual} accentColor={headerColor} autoQRSize={autoQRSize} />
              </div>
            ))}
          </div>
        ))}
        {remaining.map((el: any) => (
          <FlowElement key={el.id} el={el} lang={lang} isBilingual={effectiveBilingual} accentColor={headerColor} autoQRSize={autoQRSize} />
        ))}
      </>
    );
  })()}
</div>

{/* DATE/TIME — Fully Bilingual Bar */}
{false && (
  <div style={{
    display: 'flex',
    justifyContent: effectiveBilingual ? 'space-between' : 'flex-end',
    alignItems: 'center',
    fontSize: 10,
    color: '#475569',
    fontWeight: 600,
    marginBottom: 6,
    fontFamily: tableFont,
  }}>
    <span>📅 {invoiceDate}{invoiceTime ? `  🕐 ${invoiceTime}` : ''}</span>
    {effectiveBilingual && (
      <span style={{ direction: rtl ? 'rtl' : 'ltr', fontFamily: targetFont }}>
        📅 {(() => {
          const transDate = dynamicValuesAr?.['date'] || (lang === 'ar' ? arNum(invoiceDate) : invoiceDate);
          const rawTime = dynamicValuesAr?.['time'] || invoiceTime || '';
          const transTime = lang === 'ar' ? arNum(rawTime).replace(/\bAM\b/gi, 'ص').replace(/\bPM\b/gi, 'م') : rawTime;
          return `${transDate}${transTime ? `  🕐 ${transTime}` : ''}`;
        })()}
      </span>
    )}
  </div>
)}

{/* 👤 COMPACT CUSTOMER CARD (Strictly Enforces includeFields for Name, Email, Phone, Address, Tax ID) */}
{(() => {
  const inc = (template as any)?.includeFields || (template as any)?.include_fields || (template as any)?.elements?.[0]?.includeFields || {};

  const showName = inc.name !== false && Boolean(clientName && clientName.trim() && clientName.toLowerCase() !== 'walk-in customer');
  const showEmail = inc.email !== false && Boolean(clientEmail && clientEmail.trim());
  const showPhone = inc.phone !== false && Boolean(clientPhone && clientPhone.trim());
  const showAddress = inc.address !== false && Boolean(clientAddress && clientAddress.trim());
  const showTaxId = inc.taxId !== false && Boolean(buyerTaxId && buyerTaxId.trim());

  // If user unticked all fields, do NOT render the customer card or green vertical line at all!
  if (!showName && !showEmail && !showPhone && !showAddress && !showTaxId) {
    return null;
  }

  const enCustomerName = showName ? clientName.trim() : '';

  // Safe Fallback: NEVER allow invoice number, receipt text, or doctor label to become client's name!
  const rawFallback = (template?.elements || []).find(
    (el: any) => el.arabicContent && el.textContent && el.textContent.split('\n')[0].includes(clientName)
  )?.arabicContent?.split('\n')?.[0]?.trim();
  const isCorruptedName = /(pos-rec|invoice|receipt|إيصال|فاتورة|doctor|طبيب)/i.test(rawFallback || '');
  const safeFallbackName = !isCorruptedName ? rawFallback : undefined;

  let resolvedClientNameTr =
    clientNameAr ||
    (resolvedDynamicValues as any)?.client_name ||
    (resolvedDynamicValues as any)?.clientName ||
    safeFallbackName ||
    enCustomerName;

  // Final sanity check: if the name still contains an invoice receipt tag, discard it
  if (/(pos-rec|invoice|receipt|إيصال|فاتورة)/i.test(resolvedClientNameTr)) {
    resolvedClientNameTr = enCustomerName;
  }

  // ── 100% Dynamic Auto-Translator for Any of 100+ Languages (Zero Hardcoding) ──
  const [liveFieldLabels, setLiveFieldLabels] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!effectiveBilingual || !lang || lang === 'en') return;
    const wordsToTranslate = ['Email', 'Phone', 'Address', 'Tax ID'];
    const missing = wordsToTranslate.filter(w => {
      const cacheVal = typeof localStorage !== 'undefined' ? localStorage.getItem(`cloudairambo_label_cache_${lang}_${w}`) : null;
      return !(resolvedDynamicLabels as any)?.[w] && !(resolvedDynamicLabels as any)?.[w.toLowerCase()] && !liveFieldLabels[w] && !cacheVal;
    });

    if (missing.length === 0) return;
    let isCancelled = false;
    const apiBase = (typeof window !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || 'https://api.cloudairambo.com';
    const jwt = typeof localStorage !== 'undefined' ? localStorage.getItem('cloudairambo_jwt') || '' : '';

    missing.forEach(word => {
      fetch(`${apiBase}/translate?text=${encodeURIComponent(word)}&tl=${lang}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      })
        .then(r => r.json())
        .then(res => {
          if (!isCancelled && res?.translated && res.translated.toLowerCase() !== word.toLowerCase()) {
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(`cloudairambo_label_cache_${lang}_${word}`, res.translated);
            }
            setLiveFieldLabels(prev => ({ ...prev, [word]: res.translated }));
          }
        })
        .catch(() => {});
    });

    return () => { isCancelled = true; };
  }, [lang, effectiveBilingual, resolvedDynamicLabels]);

  const getDynamicLabel = (key: string): string => {
    if (!effectiveBilingual || !lang || lang === 'en') return key;
    const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(`cloudairambo_label_cache_${lang}_${key}`) : null;
    const val = (resolvedDynamicLabels as any)?.[key] || (resolvedDynamicLabels as any)?.[key.toLowerCase()] || liveFieldLabels[key] || cached || key;
    return String(val).replace(/[:：]$/, '').trim();
  };

  const emailLabel = getDynamicLabel('Email');
  const phoneLabel = getDynamicLabel('Phone');
  const addressLabel = getDynamicLabel('Address');
  const taxLabel = getDynamicLabel('Tax ID');

  const resolvedClientAddressTr =
    clientLineAr ||
    (resolvedDynamicValues as any)?.client_address ||
    (resolvedDynamicValues as any)?.clientAddress ||
    (resolvedDynamicValues as any)?.address ||
    (template as any)?.client_address_tr ||
    (template as any)?.clientLineAr ||
    clientAddress;

  // ⚡ Indic/Arabic digits (٠١٢٣٤٥٦٧٨٩) strictly when lang === 'ar' ONLY!
  const formattedPhoneTr = lang === 'ar' ? arNum(clientPhone) : clientPhone;
  const formattedAddressTr = lang === 'ar' ? arNum(resolvedClientAddressTr) : resolvedClientAddressTr;
  const formattedTaxIdTr = lang === 'ar' ? arNum(buyerTaxId || '') : (buyerTaxId || '');

  return (
    <div style={{
      marginBottom: 6,
      padding: '4px 7px',
      background: '#f8fafc',
      borderRadius: '0 4px 4px 0',
      borderLeft: `2.5px solid ${headerColor || '#0f766e'}`,
      fontFamily: tableFont,
      fontSize: tableFontSize,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      boxSizing: 'border-box',
    }}>
      {/* 1. Name Row */}
      {showName && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: tableFontSize, fontWeight: 700, color: '#0f172a' }}>
          <span>{enCustomerName}</span>
          {effectiveBilingual && (
            <span style={{ direction: rtl ? 'rtl' : 'ltr', color: '#0f172a', fontWeight: 700, fontFamily: targetFont }}>
              {lang === 'ar' ? arNum(resolvedClientNameTr) : resolvedClientNameTr}
            </span>
          )}
        </div>
      )}

      {/* 2. Email Row */}
      {showEmail && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: tableFontSize * 0.82, color: '#475569' }}>
          <span>Email: {clientEmail}</span>
          {effectiveBilingual && (
            <span dir={rtl ? 'rtl' : 'ltr'} style={{ fontFamily: targetFont, textAlign: rtl ? 'right' : 'left' }}>
              {emailLabel}: <span dir="ltr" style={{ fontFamily: 'monospace', display: 'inline-block' }}>{clientEmail}</span>
            </span>
          )}
        </div>
      )}

      {/* 3. Phone Row */}
      {showPhone && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: tableFontSize * 0.82, color: '#475569' }}>
          <span>Phone: {clientPhone}</span>
          {effectiveBilingual && (
            <span dir={rtl ? 'rtl' : 'ltr'} style={{ fontFamily: targetFont, textAlign: rtl ? 'right' : 'left' }}>
              {phoneLabel}: <span dir="ltr" style={{ fontFamily: 'monospace', display: 'inline-block' }}>{formattedPhoneTr}</span>
            </span>
          )}
        </div>
      )}

      {/* 4. Address Row */}
      {showAddress && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: tableFontSize * 0.82, color: '#475569' }}>
          <span>Address: {clientAddress}</span>
          {effectiveBilingual && (
            <span dir={rtl ? 'rtl' : 'ltr'} style={{ fontFamily: targetFont, textAlign: rtl ? 'right' : 'left' }}>
              {addressLabel}: <span>{formattedAddressTr}</span>
            </span>
          )}
        </div>
      )}

      {/* 5. Tax ID Row */}
      {showTaxId && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: tableFontSize * 0.82, fontWeight: 700, color: '#0f766e' }}>
          <span>Tax ID: {buyerTaxId}</span>
          {effectiveBilingual && (
            <span dir={rtl ? 'rtl' : 'ltr'} style={{ fontFamily: targetFont, textAlign: rtl ? 'right' : 'left' }}>
              {taxLabel}: <span dir="ltr" style={{ fontFamily: 'monospace', display: 'inline-block' }}>{formattedTaxIdTr}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
})()}

      {/* ITEMS TABLE */}
      <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontFamily: tableFont, fontSize: tableFontSize }}>
        <thead>
          <tr>
            {columns.map((col: any) => {
              const origHeader = col.originalHeader || col.header;
const transHeader = col.arabicHeader;  // only show if explicitly set
              return (
                <th key={col.id || col.key} style={{
                  width: `${col.width || (100 / columns.length)}%`,
                  background: headerColor,
                  color: headerColor === '#ffffff' ? '#1e293b' : '#fff',
                  padding: '6px 7px',
                  textAlign: col.align as any || 'left',
                  fontWeight: 700, fontSize: tableFontSize,
                  borderRight: '1px solid rgba(255,255,255,0.15)',
                  lineHeight: 1.3,
                }}>
                  {/* English header */}
                  <div style={{ letterSpacing: 0.2, wordBreak: 'keep-all', overflowWrap: 'normal' }}>{origHeader}</div>
                  {/* Translated header below */}
                  {effectiveBilingual && transHeader && (
                    <div style={{
                      fontSize: tableFontSize * 0.8,
                      fontFamily: rtl ? arabicFont : 'inherit',
                      direction: rtl ? 'rtl' : 'ltr',
                      textAlign: 'left',
                      opacity: 0.85, marginTop: 2,
                    }}>{transHeader}</div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {activeItems.map((item, rIdx) => (
            <tr key={item.id} style={{ background: rIdx % 2 === 1 ? '#f8fafc' : '#fff' }}>
              {columns.map((col: any) => {
                // English values
                let engVal = '';
                if (col.key === 'description') engVal = item.description;
                else if (col.key === 'qty') engVal = String(item.qty);
                else if (col.key === 'rate') engVal = `${sym}${item.rate.toFixed(2)}`;
                else if (col.key === 'total') engVal = `${sym}${(item.qty * item.rate).toFixed(2)}`;
                else if (col.key === 'sno' || col.key === 'serial' || col.key === 'index') engVal = String(rIdx + 1);
                else {
                  const snakeHeader = String(col.originalHeader || col.header || '').toLowerCase().trim().replace(/\s+/g, '_');
                  let v: any = (item as any)[col.key] ?? (item as any)[col.header] ?? (item as any)[snakeHeader];
                  if ((v === undefined || v === null || v === '') && /^(cat_tax_b2c|cat_tax_b2b)$/.test(col.key)) {
                    const rawB2b = (item as any).b2bTaxRate ?? (item as any).b2b_tax_rate;
                    const r = isB2B && rawB2b !== undefined && rawB2b !== null
                      ? parseFloat(String(rawB2b)) || 0
                      : parseFloat(String((item as any).taxRate ?? (item as any).tax_rate ?? 0)) || 0;
                    v = r > 0 ? `${r}%` : '';
                  }
                  if ((v === undefined || v === null || v === '') && col.key === 'cat_tax_name') {
                    v = isB2B
                      ? ((item as any).b2bTaxName ?? (item as any).b2b_tax_name ?? (item as any).taxName ?? (item as any).tax_name ?? '')
                      : ((item as any).taxName ?? (item as any).tax_name ?? '');
                  }
                  engVal = v === undefined || v === null ? '' : String(v);
                }

                // Translated value for numeric columns (Arabic numerals only for ar)
                const transNumVal = effectiveBilingual && isAr && col.key !== 'description'
                  ? arNum(engVal)
                  : null;

                return (
                  <td key={col.id || col.key} style={{
                    width: `${col.width || (100 / columns.length)}%`,
                    padding: '6px 7px',
                    textAlign: col.align as any || 'left',
                    fontSize: tableFontSize,
                    color: '#374151',
                    borderBottom: '1px solid #f1f5f9',
                    borderRight: '1px solid #f1f5f9',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                  }}>
                    {/* English value — always shown */}
                    <div style={{
                      fontFamily: col.key !== 'description' ? 'monospace' : 'inherit',
                      direction: 'ltr',
                      unicodeBidi: 'embed',
                      whiteSpace: col.key !== 'description' ? 'nowrap' : 'normal',
                    }}>
  {engVal}
</div>
                    {/* Translated description below */}
                    {effectiveBilingual && col.key === 'description' && item.arabicName && (
  <div style={{
    fontSize: tableFontSize * 0.85,
    fontFamily: rtl ? arabicFont : 'inherit',
    direction: rtl ? 'rtl' : 'ltr',
    // Left-aligned rather than right-aligned for RTL — sits directly
    // under the English item name instead of hugging the cell's right edge.
    textAlign: 'left',
    color: '#374151', marginTop: 1,
  }}>{isAr ? arNum(item.arabicName) : item.arabicName}</div>
)}
                    {/* Translated custom-column value (e.g. Tax Name) */}
                    {effectiveBilingual &&
                      !['description','qty','rate','total','sno','serial','index'].includes(col.key) &&
                      (item as any).customColAr?.[col.key] && (
                      <div style={{
                        fontSize: tableFontSize * 0.82,
                        fontFamily: rtl ? arabicFont : 'inherit',
                        direction: rtl ? 'rtl' : 'ltr',
                        textAlign: (col.align as any) || 'left',
                        color: '#64748b', marginTop: 1,
                      }}>{(item as any).customColAr[col.key]}</div>
                    )}
                    {/* Arabic numerals below for numeric columns */}
                    {transNumVal && transNumVal !== engVal && (
                      <div style={{
                        fontSize: tableFontSize * 0.82,
                        fontFamily: arabicFont,
                        direction: 'ltr',
                        unicodeBidi: 'embed',
                        textAlign: (col.align as any) || 'left',
                        color: '#64748b', marginTop: 1,
                      }}>{transNumVal}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {activeItems.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ padding: '8px 5px', color: '#9ca3af', textAlign: 'center', fontSize: 10 }}>
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* TOTALS (Proper Accounting Order: Subtotal ➔ Discounts ➔ Taxes ➔ Shipping ➔ Total) */}
      <div style={{ marginTop: 6, borderTop: '2px solid #e2e8f0', paddingTop: 5, fontSize: 10 }}>

        {/* 1. Subtotal (Base) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <BiLabel eng="Subtotal" trans={tl.subtotal} />
          <BiNum eng={`${sym}${subtotal.toFixed(2)}`} />
        </div>

        {/* 2. Discounts (Reduces base before tax) */}
        {discountLines.filter(d => d.rate > 0).map((d, i) => {
          const labelAr =
            (d as any).translatedLabel ||
            (d as any).labelAr ||
            (d as any).arabicLabel ||
            discountLinesAr?.find(x => x.label === d.label)?.labelAr ||
            resolvedDynamicValues?.['label_loyalty'] ||
            tl.discount;

          const rateFormatted = isAr ? `${arNum(String(d.rate))}٪` : `${d.rate}%`;
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: '#ef4444' }}>
              <BiLabel eng={`${d.label} (-${d.rate}%)`} trans={`${labelAr} (-${rateFormatted})`} />
              <BiNum eng={`-${sym}${(subtotal * d.rate / 100).toFixed(2)}`} />
            </div>
          );
        })}

        {/* 2b. Auto Loyalty Discount */}
        {autoDiscountAmount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: '#059669' }}>
            <BiLabel eng={autoDiscountLabel || 'Loyalty Discount'} trans={resolvedDynamicValues?.['label_loyalty'] || autoDiscountLabel || tl.discount} />
            <BiNum eng={`-${sym}${autoDiscountAmount.toFixed(2)}`} />
          </div>
        )}

        {/* 3. Taxes (Calculated on discounted base) */}
        {taxLines.filter(t => t.rate > 0).map((t, i) => {
          const labelAr = taxLinesAr?.find(x => x.label === t.label)?.labelAr || tl.tax;
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: '#64748b' }}>
              <BiLabel eng={`${t.label} (${t.rate}%)`} trans={`${labelAr} (${t.rate}%)`} />
              <BiNum eng={`+${sym}${(taxableValue * t.rate / 100).toFixed(2)}`} />
            </div>
          );
        })}

        {/* 3b. Named GST / VAT / Custom Breakdown */}
        {taxBreakdown && taxBreakdown.length > 0
          ? taxBreakdown.map((l, i) => {
              const customNameFromItems = activeItems.find(it => (it as any).cat_tax_name || (it.taxName && it.taxName !== 'Tax'))?.taxName || (activeItems.find(it => (it as any).cat_tax_name) as any)?.cat_tax_name;
              const resolvedName = (l.name === 'Tax' || !l.name)
                ? (productTaxName || customNameFromItems || (template as any)?.custom_tax_scheme?.name || (template as any)?.customTaxScheme?.name || 'Tax')
                : l.name;
              const resolvedTaxBreakdownAr = taxBreakdownAr || (template as any)?.tax_breakdown_ar || (template as any)?.taxBreakdownAr || (tableEl as any)?.taxBreakdownAr || (tableEl as any)?.tax_breakdown_ar || (window as any).__pos_tax_breakdown_ar || {};
              const nameTrans = resolvedTaxBreakdownAr[resolvedName] || resolvedTaxBreakdownAr[l.name] || (dynamicValuesAr as any)?.[`tax_name_${resolvedName}`] || (dynamicValuesAr as any)?.[`tax_name_${l.name}`] || (resolvedName === 'Tax' ? tl.tax : resolvedName);
              const rateStr = isAr ? `${arNum(String(l.rate))}٪` : `${l.rate}%`;
              const suffix = isB2B ? ' (ITC)' : '';

              return (
                <div key={`taxbd-${i}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: isB2B ? '#16a34a' : '#7c3aed' }}>
                  <BiLabel eng={`${resolvedName} (${l.rate}%)${suffix}`} trans={`${nameTrans} (${rateStr})${suffix}`} />
                  <BiNum eng={`+${sym}${l.amount.toFixed(2)}`} />
                </div>
              );
            })
          : productTaxTotal > 0 && (() => {
              const b2bItemName = activeItems.find(i => i.b2bTaxName || (i as any).b2b_tax_name)?.b2bTaxName || (activeItems.find(i => (i as any).cat_tax_name) as any)?.cat_tax_name;
              const b2cItemName = activeItems.find(i => (i.taxName && i.taxName !== 'Tax') || (i as any).tax_name || (i as any).cat_tax_name)?.taxName || (activeItems.find(i => (i as any).cat_tax_name) as any)?.cat_tax_name || (template as any)?.custom_tax_scheme?.name || (template as any)?.customTaxScheme?.name;
              const effectiveTaxLabel = isB2B
                ? (productTaxName || b2bItemName || 'B2B Tax')
                : (productTaxName || b2cItemName || activeItems.find(i => i.taxName)?.taxName || 'Tax');
              const resolvedTaxBreakdownAr = taxBreakdownAr || (template as any)?.tax_breakdown_ar || (template as any)?.taxBreakdownAr || (tableEl as any)?.taxBreakdownAr || (tableEl as any)?.tax_breakdown_ar || (window as any).__pos_tax_breakdown_ar || {};
              const nameTrans = resolvedTaxBreakdownAr[effectiveTaxLabel] || (dynamicValuesAr as any)?.[`tax_name_${effectiveTaxLabel}`] || (effectiveTaxLabel === 'Tax' ? tl.tax : effectiveTaxLabel);
              const ratePct = ((productTaxTotal / (subtotal || 1)) * 100).toFixed(subtotal ? 1 : 0);
              const rateStr = isAr ? `${arNum(ratePct)}٪` : `${ratePct}%`;
              const suffix = isB2B ? ' (ITC)' : '';

              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: isB2B ? '#16a34a' : '#7c3aed' }}>
                  <BiLabel
                    eng={`${effectiveTaxLabel} (${ratePct}%)${suffix}`}
                    trans={`${nameTrans} (${rateStr})${suffix}`}
                  />
                  <BiNum eng={`+${sym}${productTaxTotal.toFixed(2)}`} />
                </div>
              );
            })()}

        {/* Shipping */}
        {shippingCost > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: '#64748b' }}>
            <BiLabel eng="Shipping" trans={tl.shipping} />
            <BiNum eng={`+${sym}${shippingCost.toFixed(2)}`} />
          </div>
        )}

        {/* Grand Total */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          borderTop: '1px solid #e2e8f0', paddingTop: 5, marginTop: 3,
          fontWeight: 800, fontSize: 13,
          color: tableEl?.color && tableEl.color !== '#1e293b' ? tableEl.color : '#3b82f6',
        }}>
          <BiLabel eng="Gross Amount" trans={tl.total} />
          <BiNum eng={`${sym}${grandTotal.toFixed(2)}`} />
        </div>
      </div>

      {/* AMOUNT IN WORDS */}
      {showAmountInWords && (() => {
        const enWords = amountInWords || amountToWords(grandTotal, template?.currency || 'INR');
        const resolvedWordsAr =
          amountInWordsTranslated ||
          (dynamicValuesAr as any)?.amount_in_words ||
          (dynamicValuesAr as any)?.amountInWords ||
          (template as any)?.amount_in_words_ar ||
          (template as any)?.amountInWordsAr ||
          (window as any).__pos_amount_in_words_ar;

        return (
          <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed #e2e8f0', fontSize: 9, color: '#374151' }}>
            <div style={{ fontStyle: 'italic' }}>
              {enWords}
            </div>
            {effectiveBilingual && resolvedWordsAr && (
              <div style={{
                fontStyle: 'italic', marginTop: 2,
                fontFamily: rtl ? arabicFont : 'inherit',
                direction: rtl ? 'rtl' : 'ltr',
                textAlign: rtl ? 'right' : 'left',
              }}>
                {isAr ? arNum(resolvedWordsAr) : resolvedWordsAr}
              </div>
            )}
          </div>
        );
      })()}

      {/* STATUS STAMP (Clean, Professional Bilingual Stamp) */}
      {statusColor && invoiceStatus && (() => {
        const isPaid = ['paid','Paid'].includes(invoiceStatus || '');
        const statusText = isPaid ? 'PAID IN FULL' : (invoiceStatus || '').toUpperCase();
        const statusTrans =
          (resolvedDynamicValues as any)?.['status_stamp'] ||
          (resolvedDynamicValues as any)?.[statusText] ||
          (resolvedDynamicValues as any)?.[(invoiceStatus || '').toLowerCase()] ||
          (isPaid ? (lang === 'hi' ? 'पूर्ण भुगतान' : lang === 'ar' ? 'مدفوع بالكامل' : '') : '');

        const hasTranslation = effectiveBilingual && statusTrans && statusTrans.trim().toLowerCase() !== statusText.trim().toLowerCase();

        return (
          <div style={{
            textAlign: 'center', marginTop: 10,
            border: `3px double ${statusColor}`, borderRadius: 6, padding: '5px 8px',
            color: statusColor, fontWeight: 900, fontSize: 13, letterSpacing: 2,
            textTransform: 'uppercase',
          }}>
            <div>{statusText}</div>
            {hasTranslation && (
              <div style={{
                fontSize: 10.5,
                fontFamily: targetFont,
                direction: rtl ? 'rtl' : 'ltr',
                fontWeight: 700,
                letterSpacing: 0,
                marginTop: 2,
                opacity: 0.95,
              }}>
                {isAr ? arNum(statusTrans) : statusTrans}
              </div>
            )}
          </div>
        );
      })()}

      {/* NOTES */}
      {notes && (
        <div style={{ marginTop: 8, fontSize: 9, color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: 5 }}>
          <div>{notes}</div>
          {effectiveBilingual && notesAr && (
            <div style={{
              fontFamily: rtl ? arabicFont : 'inherit',
              direction: rtl ? 'rtl' : 'ltr',
              textAlign: rtl ? 'right' : 'left',
              marginTop: 2,
            }}>
              {notesAr}
            </div>
          )}
        </div>
      )}
      {/* FOOTER ELEMENTS */}
{footerEls.length > 0 && (
  <div style={{ marginTop: isThermal ? 4 : 8, borderTop: '1px dashed #e2e8f0', paddingTop: isThermal ? 4 : 6 }}>
    {footerEls.map((el: any) => (
      <FlowElement key={el.id} el={el} lang={lang} isBilingual={effectiveBilingual} accentColor={headerColor} autoQRSize={autoQRSize} />
    ))}
  </div>
)}
{/* PREVIEW PLACEHOLDER: Shows in Quick Invoice & POS (isEditPreview is false) so you can test Left / Center / Right alignment and size */}
      {!isEditPreview &&
        !footerEls.some((el: any) => el.type === 'qr' && (el._auto === 'invoice' || el.qrValue?.includes('/invoice/view/'))) &&
        !footerEls.some((el: any) => el.type === 'image' && (el.id?.startsWith('invoice-qr') || el.id?.startsWith('token-qr'))) &&
        (() => {
          const currentInvNo = dynamicValues?.invoice_number || 'PREVIEW';
          const qrAlign = (template as any)?.qr_position || (typeof window !== 'undefined' ? (localStorage.getItem('qi_auto_qr_position') as any) : null) || 'center';
          const qrSize = typeof window !== 'undefined' ? parseInt(localStorage.getItem('qi_auto_qr_size') || '80') : 80;
          const matrix = generateQRMatrix(`${window.location.origin}/?i=${encodeURIComponent(currentInvNo)}`);
          const cell = 2;
          const size = Math.min(qrSize, matrix.length * cell);

          return (
            <div style={{
              marginTop: 10,
              borderTop: '1px dashed #e2e8f0',
              paddingTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: qrAlign === 'left' ? 'flex-start' : qrAlign === 'right' ? 'flex-end' : 'center',
              textAlign: qrAlign,
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#000000', marginBottom: 4 }}>
                Scan to view invoice
                {effectiveBilingual && (window as any).__pos_qr_invoice_label && (window as any).__pos_qr_invoice_label !== 'Scan to view invoice' && (
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#000000', fontFamily: rtl ? arabicFont : 'inherit', direction: rtl ? 'rtl' : 'ltr', marginTop: 1 }}>
                    {(window as any).__pos_qr_invoice_label}
                  </div>
                )}
              </div>
              <svg width={size} height={size} style={{ display: 'block' }}>
                {matrix.map((row: boolean[], r: number) =>
                  row.map((on: boolean, c: number) =>
                    on ? <rect key={`${r}-${c}`} x={c * cell} y={r * cell} width={cell} height={cell} fill="#000000" /> : null
                  )
                )}
              </svg>
              <div style={{ fontSize: 8, color: '#9ca3af', marginTop: 4 }}>
                Invoice QR — generated on save
              </div>
            </div>
          );
        })()}
     

    </div>
  );
}